import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatToolbar } from '@angular/material/toolbar';
import { type IIntegration } from '@principle-theorem/integrations';
import {
  BasicDialogService,
  ButtonsContainerComponent,
  DialogPresets,
  IIntegrationDialogData,
} from '@principle-theorem/ng-shared';
import { ParchmentIntegrationStorage } from '@principle-theorem/principle-core';
import {
  IOrganisation,
  IParchmentIntegrationData,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  httpsCallable,
  serialise,
  snapshot,
  WithRef,
} from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { OrganisationService } from '../../../../organisation.service';
import {
  IParchmentFormData,
  ParchmentIntegrationFormComponent,
} from '../parchment-integration-form/parchment-integration-form.component';

@Component({
  selector: 'pr-manage-parchment-integration',
  templateUrl: './manage-parchment-integration.component.html',
  styleUrls: ['./manage-parchment-integration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatToolbar,
    MatDialogModule,
    ButtonsContainerComponent,
  ],
})
export class ManageParchmentIntegrationComponent {
  private _storage = new ParchmentIntegrationStorage();
  private _organisation$: Observable<WithRef<IOrganisation>>;
  private _data =
    inject<IIntegrationDialogData<IParchmentIntegrationData>>(MAT_DIALOG_DATA);
  integration$: Observable<IIntegration<IParchmentIntegrationData> | undefined>;

  constructor(
    private _dialog: BasicDialogService,
    private _matSnackbar: MatSnackBar,
    private _organisation: OrganisationService
  ) {
    this.integration$ = this._storage.get$(this._data.integrationsCol);
    this._organisation$ =
      this._organisation.organisation$.pipe(filterUndefined());
  }

  async removeIntegration(): Promise<void> {
    const confirmed = await this._dialog.confirm({
      prompt:
        'Are you sure you want to remove the integration with Parchment? You will no longer be able to view/send prescriptions via Parchment.',
      title: 'Remove',
      submitLabel: 'Yes, Remove',
      submitColor: 'warn',
    });

    if (!confirmed) {
      return;
    }

    await this._storage.remove(this._data.integrationsCol);
  }

  async addIntegration(): Promise<void> {
    const data = await this._dialog
      .open<ParchmentIntegrationFormComponent, undefined, IParchmentFormData>(
        ParchmentIntegrationFormComponent,
        DialogPresets.medium()
      )
      .afterClosed()
      .toPromise();

    if (!data) {
      return;
    }

    try {
      await this._storageAdd(data);

      const organisation = await snapshot(this._organisation$);
      await snapshot(
        httpsCallable('http-parchment-validate')(
          serialise({
            organisationRef: organisation.ref,
          })
        )
      );

      this._matSnackbar.open(
        'Parchment integration has been successfully enabled'
      );
    } catch {
      this._matSnackbar.open(
        'Failed to add Parchment integration. Please try again.'
      );
    }
  }

  private async _storageAdd(data: IParchmentIntegrationData): Promise<void> {
    await this._storage.add(this._data.integrationsCol, data);
  }
}
